import { AlignCenterIcon, LocationPinIcon, TextCaseIcon } from "@/assets/icons";

/**
 * Router path for Agentic AI. `useLocation` reports it with the `basename`
 * already stripped, so never run this through `withBasePath` when comparing
 * against a location — only when building a raw `<a href>`, which the router
 * does not resolve.
 */
export const AGENTIC_AI_ROUTE = "/agentic-ai";

export const AGENTIC_AI_AGENT_TOPIC = "analysis_run" as const;

/**
 * Path of the search chats page. A static segment ranks above `:chatId`, so
 * "search" is never read as a conversation id. The path has no id: the chat
 * that was open stays mounted behind the page (see `useAgenticAI`).
 */
export const AGENTIC_AI_SEARCH_ROUTE = `${AGENTIC_AI_ROUTE}/search`;

/**
 * Route pattern with the open conversation in the path. The id is optional:
 * the bare route is the welcome screen. Opening from outside, e.g. the "Go to
 * chat" link on the Analysis History detail panel, uses `/agentic-ai/<id>`.
 */
export const AGENTIC_AI_CHAT_ROUTE = `${AGENTIC_AI_ROUTE}/:chatId?`;

/**
 * The chat history sections, newest first. A chat belongs to the first bucket
 * whose `maxDaysAgo` it fits in. The day counts follow the product spec: with
 * today as 2 Oct, "Previous 7 days" is 25–30 Sep and "Previous 30 days" is
 * 2–24 Sep.
 */
export const CHAT_BUCKETS = [
  { key: "today", label: "Today", maxDaysAgo: 0 },
  { key: "yesterday", label: "Yesterday", maxDaysAgo: 1 },
  { key: "previous7Days", label: "Previous 7 days", maxDaysAgo: 7 },
  { key: "previous30Days", label: "Previous 30 days", maxDaysAgo: 30 },
  { key: "older", label: "Older", maxDaysAgo: Infinity }
] as const;

export type ChatBucket = (typeof CHAT_BUCKETS)[number]["key"];

/**
 * One entry in the chat history sidebar.
 *
 * `bucket` is worked out by the container so the list stays presentational —
 * it never touches a clock.
 */
export type ChatHistoryEntry = {
  id: string;
  title: string;
  bucket: ChatBucket;
};

/** A search result row. It shows its own time label, not a history bucket. */
export type ChatSearchRow = {
  id: string;
  title: string;
  preview: string;
  timeLabel: string;
};

export type ChatListLoadMore = {
  /** Callback ref for the element at the end of the list. */
  ref: (node: HTMLElement | null) => void;
  isFetching: boolean;
  /** A later page failed. The rows stay and the footer offers a retry. */
  isError: boolean;
  onRetry: () => void;
};

export const AGENTIC_AI_SIDEBAR_TITLE = "AI Intelligence";

/** Fallback while neither the SDK session nor a selected conversation has a
 * name yet. */
export const AGENTIC_AI_UNTITLED_CHAT = "New chat";

export const AGENTIC_AI_CHAT_HISTORY_HEADING = "Chats";

export const AGENTIC_AI_CHAT_HISTORY_EMPTY =
  "No chats yet. Start a conversation and it will show up here.";

export const AGENTIC_AI_CHAT_HISTORY_ERROR = "Couldn't load your chats.";

export const AGENTIC_AI_CHAT_HISTORY_RETRY = "Try again";

export const AGENTIC_AI_LOAD_MORE_ERROR = "Couldn't load more chats.";

export const AGENTIC_AI_HEADING = "What would you like to explore today?";

export const AGENTIC_AI_DESCRIPTION =
  "Ask anything about locations, markets, competitors, datasets, and business opportunities.";

export const AGENTIC_AI_INPUT_PLACEHOLDER =
  "Ask Agentic AI anything about locations...";

export const AGENTIC_AI_HEADER_PADDING_Y = "py-[1.05rem]";

export const AGENTIC_AI_SEARCH_HEADING = "Search chats";

export const AGENTIC_AI_SEARCH_DESCRIPTION =
  "Find previous conversations by title or content.";

export const AGENTIC_AI_SEARCH_PLACEHOLDER =
  "Search chats by title, location, market, competitor, or keyword";

export const AGENTIC_AI_RECENT_SEARCHES_HEADING = "Recent searches";

export const AGENTIC_AI_RECENT_SEARCHES_DESCRIPTION =
  "Quickly pick up where you left off.";

export const AGENTIC_AI_RECENT_SEARCHES_EMPTY =
  "You don't have any recent search";

export const AGENTIC_AI_RECENT_SEARCH_LABEL = "Search again for";

export const AGENTIC_AI_SEARCH_TIPS_HEADING = "Search tips";

export const AGENTIC_AI_SEARCH_TIPS_DESCRIPTION =
  "Use location names, business categories, competitors, or topics such as market trends and customer behavior.";

export const AGENTIC_AI_SEARCH_ERROR = "Couldn't load your search results.";

export const AGENTIC_AI_SEARCH_NO_RESULTS = "No results for";

export const AGENTIC_AI_SEARCH_NO_RESULTS_STATUS = "No results found for";

export const AGENTIC_AI_SEARCH_ERROR_STATUS =
  "Search failed. Use Try again to retry.";

export const AGENTIC_AI_SEARCH_SUGGESTIONS_HEADING = "Try these suggestions:";

export const AGENTIC_AI_SEARCH_SUGGESTIONS = [
  {
    icon: TextCaseIcon,
    title: "Check your spelling",
    description: "Make sure all words are spelled correctly."
  },
  {
    icon: AlignCenterIcon,
    title: "Try a shorter keyword",
    description: "Use fewer or more general terms."
  },
  {
    icon: LocationPinIcon,
    title: "Search by location, competitor, or market topic",
    description: "For example: “coffee shops in Papua” or “F&B expansion”."
  }
];
